// Filter defaults + URL search-param (de)serialization for the dashboard.
//
// FILT-02: defaults written to URL on first mount via setSearchParams(defaults, { replace: true })
// FILT-03: useFilters is the sole writer; this module is its serializer.
//
// All functions are pure — no React, no DOM. Validates and clamps untrusted URL input
// per T-05-14 mitigation in plan 05-05 threat model.

import type { FilterState, SortKey, RecencyFilter } from '@/types/db';
import {
  NEAREST_CATEGORY_VALUES,
  type NearestCategory,
} from '@/lib/nearest-constants';

/** Canonical category slugs accepted server-side. */
export const ALL_CATEGORIES = ['dog_park', 'beach', 'daycare', 'bar'] as const;
export type CategorySlug = (typeof ALL_CATEGORIES)[number];

/**
 * Shared base. Page-specific default sets are derived from this so the two
 * pages can seed different starting states while sharing the same URL schema.
 */
const BASE_FILTERS: FilterState = {
  radius: 0.5,
  // Dashboard default: newest-first. (Map ignores sort.)
  sort: 'recency',
  // Amenity targeted by sort:'nearest'. Inert for every other sort.
  nearestCat: 'dog_park',
  county: [],
  recency: 'all',
  cats: ['dog_park', 'beach', 'daycare', 'bar'],
  // Phase 08 (Plan 08-02) — map-only highlight flag. Default ON so first-time
  // visits to /app/map show the in-radius lighting without action.
  highlight: true,
  // Phase 08-r (Plan 260502-08r) — map-only filter. Default OFF so first-time
  // visitors see every property; the user opts in via the chip when they want
  // to focus on in-radius properties only.
  inRadiusOnly: false,
  // Phase 08-r — radius-rings toggle. Default OFF — with 539 amenities and
  // overlapping circles the geometry is visually noisy unless explicitly
  // requested.
  showRadiusCircles: false,
  // Phase 08-r — amenity-pins toggle. Default OFF per user spec ("I should
  // never see a pin"). Classification still runs so the radius-based
  // property filters work without showing the pins.
  showAmenityPins: false,
};

/** Dashboard default set: recency sort, all dog categories, every property shown. */
export const DASHBOARD_DEFAULTS: FilterState = { ...BASE_FILTERS };

/**
 * Map default set. On a fresh visit the map shows only properties within
 * 0.5mi of a dog park or dog beach — dog_park+beach categories, inRadiusOnly
 * on, highlight on. (`useFilters(MAP_DEFAULTS)` seeds this on bare /app/map.)
 */
export const MAP_DEFAULTS: FilterState = {
  ...BASE_FILTERS,
  cats: ['dog_park', 'beach'],
  inRadiusOnly: true,
  highlight: true,
};

/**
 * Back-compat alias — the dashboard's default set. Existing imports of
 * DEFAULT_FILTERS (useFilters, activeChips, tests) resolve to dashboard defaults.
 */
export const DEFAULT_FILTERS: FilterState = DASHBOARD_DEFAULTS;

const VALID_SORTS: readonly SortKey[] = ['composite', 'dog', 'walk_ent', 'recency', 'price', 'nearest'];
const VALID_RECENCY: readonly RecencyFilter[] = ['all', '7d', '30d', '90d'];

const MIN_RADIUS = 0.1;
const MAX_RADIUS = 3.0;

function clampRadius(n: number): number {
  if (!Number.isFinite(n)) return BASE_FILTERS.radius;
  if (n < MIN_RADIUS) return MIN_RADIUS;
  if (n > MAX_RADIUS) return MAX_RADIUS;
  // Round to one decimal so URL state matches slider step (0.1).
  return Math.round(n * 10) / 10;
}

/**
 * Parse a URLSearchParams (or compatible) into a FilterState. Untrusted input.
 *
 * - radius: parseFloat → clamp [0.1, 3.0] → fall back to default
 * - sort: validated against VALID_SORTS (T-05-14)
 * - county: csv split, lowercased, empties stripped
 * - recency: validated against VALID_RECENCY
 * - cats: csv split, validated against ALL_CATEGORIES; empty/invalid → defaults
 */
export function parseFilters(
  sp: URLSearchParams,
  defaults: FilterState = DASHBOARD_DEFAULTS,
): FilterState {
  const rawRadius = sp.get('radius');
  const radius = rawRadius == null ? defaults.radius : clampRadius(parseFloat(rawRadius));

  const rawSort = sp.get('sort');
  const sort: SortKey = (VALID_SORTS as readonly string[]).includes(rawSort ?? '')
    ? (rawSort as SortKey)
    : defaults.sort;

  const rawCounty = sp.get('county');
  const county = rawCounty
    ? rawCounty
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s.length > 0)
    : [];

  const rawRecency = sp.get('recency');
  const recency: RecencyFilter = (VALID_RECENCY as readonly string[]).includes(rawRecency ?? '')
    ? (rawRecency as RecencyFilter)
    : defaults.recency;

  const rawCats = sp.get('cats');
  let cats: string[];
  if (rawCats) {
    const parsed = rawCats
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter((s): s is CategorySlug =>
        (ALL_CATEGORIES as readonly string[]).includes(s),
      );
    cats = parsed.length > 0 ? parsed : defaults.cats.slice();
  } else {
    cats = defaults.cats.slice();
  }

  // Phase 08 (Plan 08-02) — strict validation per T-08-06. Only literal
  // 'true' or 'false' accepted; missing or any other string falls back to
  // the default (true). This keeps URL state tampering bounded.
  const rawHighlight = sp.get('highlight');
  let highlight: boolean;
  if (rawHighlight === 'false') {
    highlight = false;
  } else if (rawHighlight === 'true') {
    highlight = true;
  } else {
    highlight = defaults.highlight;
  }

  // Phase 08-r (Plan 260502-08r) — strict 'true'/'false' validation, same
  // shape as `highlight`. Missing/invalid → default (false).
  const rawInRadiusOnly = sp.get('inRadiusOnly');
  let inRadiusOnly: boolean;
  if (rawInRadiusOnly === 'true') {
    inRadiusOnly = true;
  } else if (rawInRadiusOnly === 'false') {
    inRadiusOnly = false;
  } else {
    inRadiusOnly = defaults.inRadiusOnly;
  }

  // Phase 08-r — strict 'true'/'false' validation. Missing/invalid → default (false).
  const rawShowRadiusCircles = sp.get('showRadiusCircles');
  let showRadiusCircles: boolean;
  if (rawShowRadiusCircles === 'true') {
    showRadiusCircles = true;
  } else if (rawShowRadiusCircles === 'false') {
    showRadiusCircles = false;
  } else {
    showRadiusCircles = defaults.showRadiusCircles;
  }

  const rawShowAmenityPins = sp.get('showAmenityPins');
  let showAmenityPins: boolean;
  if (rawShowAmenityPins === 'true') {
    showAmenityPins = true;
  } else if (rawShowAmenityPins === 'false') {
    showAmenityPins = false;
  } else {
    showAmenityPins = defaults.showAmenityPins;
  }

  // nearestCat — amenity targeted by sort:'nearest'. Validated against the
  // 7-value enum; missing/invalid falls back to the page default.
  const rawNearestCat = sp.get('nearestCat');
  const nearestCat: NearestCategory = (NEAREST_CATEGORY_VALUES as readonly string[]).includes(
    rawNearestCat ?? '',
  )
    ? (rawNearestCat as NearestCategory)
    : defaults.nearestCat;

  return { radius, sort, nearestCat, county, recency, cats, highlight, inRadiusOnly, showRadiusCircles, showAmenityPins };
}

/**
 * Serialize a FilterState to a URLSearchParams.
 * - radius is always 1 decimal ('0.5')
 * - sort/recency always present
 * - county omitted when empty (cleaner URLs; parseFilters defaults [])
 * - cats always present (so deep-link is unambiguous about which cats are active)
 */
export function serializeFilters(filters: FilterState): URLSearchParams {
  const sp = new URLSearchParams();
  sp.set('radius', filters.radius.toFixed(1));
  sp.set('sort', filters.sort);
  // nearestCat only matters (and only round-trips) under the nearest sort.
  if (filters.sort === 'nearest') {
    sp.set('nearestCat', filters.nearestCat);
  }
  if (filters.county.length > 0) {
    sp.set('county', filters.county.join(','));
  }
  sp.set('recency', filters.recency);
  sp.set('cats', filters.cats.join(','));
  // Phase 08 (Plan 08-02) — always emitted so deep-links are unambiguous about
  // whether properties should be highlighted on /app/map.
  sp.set('highlight', filters.highlight ? 'true' : 'false');
  // Phase 08-r (Plan 260502-08r) — always emitted so deep-links unambiguously
  // round-trip the filter (vs `highlight`-style restyling).
  sp.set('inRadiusOnly', filters.inRadiusOnly ? 'true' : 'false');
  sp.set('showRadiusCircles', filters.showRadiusCircles ? 'true' : 'false');
  sp.set('showAmenityPins', filters.showAmenityPins ? 'true' : 'false');
  return sp;
}

/**
 * Deep-equality test for FilterState — used by useFilters to short-circuit
 * URL writes when no field actually changed (avoids history-entry spam from
 * idempotent setFilters calls).
 */
export function filtersEqual(a: FilterState, b: FilterState): boolean {
  if (a.radius !== b.radius) return false;
  if (a.sort !== b.sort) return false;
  // nearestCat only affects behavior under the nearest sort.
  if (a.sort === 'nearest' && a.nearestCat !== b.nearestCat) return false;
  if (a.recency !== b.recency) return false;
  if (a.county.length !== b.county.length) return false;
  for (let i = 0; i < a.county.length; i++) {
    if (a.county[i] !== b.county[i]) return false;
  }
  if (a.cats.length !== b.cats.length) return false;
  for (let i = 0; i < a.cats.length; i++) {
    if (a.cats[i] !== b.cats[i]) return false;
  }
  if (a.highlight !== b.highlight) return false;
  if (a.inRadiusOnly !== b.inRadiusOnly) return false;
  if (a.showRadiusCircles !== b.showRadiusCircles) return false;
  if (a.showAmenityPins !== b.showAmenityPins) return false;
  return true;
}

/**
 * Returns the keys whose values differ between `next` and `prev`. Used by
 * useFilters to detect "slider-only" changes (debounce target). For `county`
 * and `cats` we compare by joined string for cheap stable equality.
 */
export function diffFilterKeys(prev: FilterState, next: FilterState): (keyof FilterState)[] {
  const changed: (keyof FilterState)[] = [];
  if (prev.radius !== next.radius) changed.push('radius');
  if (prev.sort !== next.sort) changed.push('sort');
  if (prev.nearestCat !== next.nearestCat) changed.push('nearestCat');
  if (prev.recency !== next.recency) changed.push('recency');
  if (prev.county.join(',') !== next.county.join(',')) changed.push('county');
  if (prev.cats.join(',') !== next.cats.join(',')) changed.push('cats');
  if (prev.highlight !== next.highlight) changed.push('highlight');
  if (prev.inRadiusOnly !== next.inRadiusOnly) changed.push('inRadiusOnly');
  return changed;
}
